import { Box, debounce, Stack, Typography } from '@mui/material'
import {
  CarrierVisitDirection,
  CarrierVisitStatus,
  TruckVisitWithOrdersDto,
} from '@planning/app/api'
import { useSelectedGateLabel } from '@planning/pages/GateClerk/hooks/useSelectedGateLabel'
import { HeaderAvatar, NavigationStackBackButton } from '@planning/pages/TallymanV2/Components'
import { ContainerSearchLoader } from '@planning/pages/TallymanV2/Components/ContainerSearchLoader'
import { truckVisitService } from '@planning/services'
import { ContainerMobile, Header, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { useCallback, useMemo, useState } from 'react'
import { usePlanningStore } from '@planning/AppProvider'
import { fetchHoldTypes } from '@planning/utils/hold-enforcement'
import { TruckVisitSearchAlert } from '../Molecules/TruckVisitSearchAlert'
import { TruckVisitSearchField } from '../Molecules/TruckVisitSearchField'
import {
  TruckAppointmentSearchBy,
  TruckAppointmentSearchResult,
} from '../TruckAppointmentSearchResult'

interface Props {
  label: string
  direction: CarrierVisitDirection
  onSelectVisit: (selectedVisit: TruckVisitWithOrdersDto | null) => void
}
export const GateClerkSelectTruckVisit = observer(({ label, direction, onSelectVisit }: Props) => {
  const { holdItemStore } = usePlanningStore()
  const theme = useMinimalsTheme()
  const gateLabel = useSelectedGateLabel()

  const [truckVisits, setTruckVisits] = useState<TruckVisitWithOrdersDto[]>([])
  const [filter, setFilter] = useState<string>('')
  const [searchBy, setSearchBy] = useState<TruckAppointmentSearchBy>(
    TruckAppointmentSearchBy.TruckPlate,
  )
  const [isSearched, setIsSearched] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const onSearch = useCallback(
    async (filter: string, searchBy: TruckAppointmentSearchBy) => {
      const status =
        direction === CarrierVisitDirection.Inbound
          ? CarrierVisitStatus.Expected
          : CarrierVisitStatus.Arrived
      let newItems: TruckVisitWithOrdersDto[] = []

      setIsSearched(false)
      setIsLoading(!!filter)
      if (filter) {
        if (searchBy === TruckAppointmentSearchBy.DriverID) {
          newItems = await truckVisitService.getByDriverId(filter, status)
        } else {
          newItems = await truckVisitService.getByLicensePlate(filter, status)
        }
      }

      const journeyIds = newItems
        .flatMap(v => v.containerJourneys ?? [])
        .map(j => j.id)
        .filter((id): id is number => id !== undefined)

      await Promise.all([
        fetchHoldTypes(),
        journeyIds.length > 0
          ? holdItemStore.fetchHoldsByContainerJourneyIds(journeyIds)
          : Promise.resolve(),
      ])

      setIsLoading(false)
      setIsSearched(true)
      setTruckVisits(newItems)
    },
    [direction, holdItemStore],
  )

  const debouncedSendRequest = useMemo(() => debounce(onSearch, 500), [onSearch])

  const handleSearch = (filter: string, searchBy: TruckAppointmentSearchBy) => {
    setFilter(filter)
    setSearchBy(searchBy)
    debouncedSendRequest(filter, searchBy)
  }

  const handleClearFilter = () => {
    setFilter('')
    setTruckVisits([])
    setIsSearched(false)
    setIsLoading(false)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.grey[100],
      }}
    >
      <Header
        leftRenderOption={
          <Stack gap={2} width='100%' paddingY={1}>
            <Stack flexDirection='row' justifyContent='space-between' alignItems='center'>
              <Stack flexDirection='row' gap={1} alignItems='center'>
                <NavigationStackBackButton />
                <Stack>
                  <Typography variant='subtitle2'>{label}</Typography>
                  {gateLabel && (
                    <Typography variant='caption' color='text.secondary'>
                      {gateLabel}
                    </Typography>
                  )}
                </Stack>
              </Stack>
              <HeaderAvatar />
            </Stack>
            <TruckVisitSearchField onSearch={handleSearch} onClearFilter={handleClearFilter} />
          </Stack>
        }
      />
      <ContainerMobile>
        <Stack gap={2} paddingY={2}>
          {isLoading ? (
            <ContainerSearchLoader />
          ) : (
            <>
              {truckVisits.map(truckVisit => (
                <TruckAppointmentSearchResult
                  key={`truck-visit-search-result-${truckVisit.id}`}
                  onClick={onSelectVisit}
                  truckVisitWithOrdersDto={truckVisit}
                  searchBy={searchBy}
                  highlight={filter}
                  direction={direction}
                />
              ))}

              {truckVisits.length === 0 && isSearched && filter && (
                <TruckVisitSearchAlert filter={filter} direction={direction} />
              )}
            </>
          )}
        </Stack>
      </ContainerMobile>
    </Box>
  )
})
